<template>
  <main>
    <big-loader v-if="isLoading"></big-loader>
    <section v-else ref="gainers">

      <DataGridTable :is-compact="true"
                     :key="tableKey"
                     :data="list"
                     :hovered-columns="true"
                     :loaded="isLoading"
                     :height-decrease="67"
                     :default-sort="defaultSort"
                     :heightElements="['calendarFilterPanel']"
                     :extra-headers="extraHeaders"
                     :default-sort-direction="defaultSortDirection"
                     :fixed-col="0">
        <template #thead="{thClick, sortKey, sortDirection, xAxisScrolled, toggleSymbolActions, showSymbolActions, headers}">
          <HeadCell v-for="(th, thIndex) in headers"
                    :sortKey="sortKey"
                    :header="th"
                    :sortDirection="sortDirection"
                    :sortValue="th.sortValue"
                    :showSymbolActions="showSymbolActions"
                    @toggleSymbolActions="toggleSymbolActions()"
                    @sort="thClick(th.sortValue, thIndex)">
            {{ th.label }}
          </HeadCell>
        </template>

        <template #tbody="{rows, showSymbolActions}">
          <tr v-for="(row, rowIndex) in rows" :key="`r-${rowIndex}`" :class="{inWatchlistMark: row.inWatchlistMark}">
            <td>
              <div style="position: relative">
              <symbol-tag :tags="row.tags" :options="{link: true, chart: true, logo: 'small'}"  :showSymbolActions="showSymbolActions" :symbol="row.symbol"/>
              </div>
            </td>
            <td>
              {{ formatDate(row.exDate) }}
            </td>
            <td>
            {{ row.dividend }}
            </td>
            <td>
              {{ formatDate(row.paymentDate) }}
            </td>
            <!--            <td>{{ row.name }}</td>-->
            <td>
              <symbol-label :key="`s-${row.symbol}`"
                            :pre-market-quote="row?.preMarket"
                            :post-market-quote="row?.postMarket"
                            :symbol="row.tradingData" :price-only="true"
                            :show-prePost="true" :always-on="true"/>
            </td>
           <VolumeCell :volume="row.tradingData?.formattedVolume" :trend="row.tradingData?.volumeTrend" />

            <td>
              {{ row.tradingData?.formattedMarketCap }}
            </td>
              <NumericCell
                :value="row.tradingData?.atr?.atrPercent"
                :ranges="[0, 1, 3, 5, 7]"
                :classes="['low','moderate','medium','high','extreme']"
            />
            <TrendIconCell :trend="row.tradingData?.atr?.atrTrend"/>
            <TrendIconCell :trend="row.tradingData?.obvTrend"/>

            <NumericCell v-for="period in ['5D','10D','1M']" :value="row.eods?.[period]"/>
            <PriceDistance :tag="'td'" :symbol="row.symbol" :threshold="3" :previousClose="row.tradingData?.price" :direction="'top'"
                           :defaultPrice="row.tradingData?.price" :value="row.tradingData?.ma50"
                           @deltaChange="deltaChange(row.symbol, $event, 'ma50D')"/>
            <PriceDistance :tag="'td'" :symbol="row.symbol" :threshold="3" :previousClose="row.tradingData?.price" :direction="'bottom'"
                           :defaultPrice="row.tradingData?.price" :value="row.tradingData?.ma200"
                           @deltaChange="deltaChange(row.symbol, $event, 'ma200D')"/>

            <YearlyRangeCell :delta-change="this.deltaChange" :row="row"/>
            <RSICell :rsi="row.tradingData?.rsi"/>
            <MACDCell :macd="row.tradingData?.macd"/>
          
            <NumericCell :noColor="true" :value="row.tradingData?.eps"/>
            <NumericCell :noColor="true" :value="row.tradingData?.pe"/>
            <IndustrySectorCell :symbol="row"/>
          </tr>
        </template>
      </DataGridTable>

    </section>
  </main>
</template>

<script>

import TopTen from '../../services/topTen';
import SymbolLabel from "../../components/symbols/symbol";
import BigLoader from "../../components/ui/BigLoader";
import DataGridTable from "../../components/dataGrid/DataGridTable";
import Tabs from "../../components/ui/Tabs";
import HeadCell from "../../components/dataGrid/HeadCell";
import SymbolLogo from "../../components/symbols/symbolLogo";
import UserService from "../../services/user";
import {useWatchlist} from "../../stores/watchlist";
import DropDown from "../../components/ui/DropDown";
import SymbolLink from "../../components/symbols/symbolLink";
import BigNote from "../../components/ui/BigNote";

import quoteStreaming from "../../composables/quoteStreaming";
import ButtonsGroup from "../../components/ui/ButtonsGroup";
import {useNewYorkTime} from "../../stores/newYorkTime";
import {DateTime} from "luxon";
import PriceDistance from "../priceTags/priceDistance";
import SymbolChart from "../symbols/symbolChart";
import SymbolTag from "../symbols/symbolTag";
import PriceRange from "../priceTags/priceRange";
import NumericCell from "../dataGrid/NumericCell";
import YearlyRangeCell from "../dataGrid/YearlyRangeCell";
import TrendIconCell from "../dataGrid/TrendIconCell";
import MACDCell from "../dataGrid/MACDCell";
import RSICell from "../dataGrid/RSICell";
import IndustrySectorCell from "../dataGrid/IndustrySectorCell";
import VolumeCell from "../dataGrid/VolumeCell";

export default {
  name: "DividendsCalendar",
  components: {
    IndustrySectorCell,
    RSICell,
    MACDCell,
    TrendIconCell,
    YearlyRangeCell,
    NumericCell,
    PriceRange,
    SymbolTag,
    SymbolChart,
    PriceDistance,
    ButtonsGroup,
    BigNote, SymbolLink, DropDown, SymbolLogo, HeadCell, Tabs, DataGridTable, BigLoader, SymbolLabel, VolumeCell
  },
  props: ['prices', 'isMini', 'calendar'],
  setup() {

    const marketStatus = useNewYorkTime();
    const {updateStream, leaveStream} = quoteStreaming();
    return {updateStream, leaveStream, marketStatus};
  },
  data() {
    return {
      watchlist: useWatchlist(),
      tab: 'market',
      defaultSort: 'changeP',
      extraHeaders: [{
        position: 1,
        label: this.dictionary.calendars.headers.exDate,
        sortValue: 'date'
      }, {
        position: 2,
        label: this.dictionary.calendars.headers.dividend,
        sortValue: 'dividend'
      }, {
        position: 3,
        label: this.dictionary.calendars.headers.payableDate,
        sortValue: 'paymentDate'
      }],
      defaultSortDirection: -1,
      tableKey: 0,
      isLoading: false,
      loadingInterval: null
    }
  },
  computed: {
    earnings() {

      let earnings = {};

      for (let k in this.calendar) {
        earnings[k] = this.calendar[k];
        earnings[k].events = earnings[k].events.sort((a, b) => {
          return a.releaseTime.localeCompare(b.releaseTime);
        });
      }

    },
    showPrePost() {

      if ((this.marketStatus.isPreMarket || this.marketStatus.isPostMarket) && this.activeButton !== 'tradingHours') return true;
      if (this.activeButton !== 'tradingHours') return true;
    },
    list() {
      if (!this.calendar) return [];
      return this.calendar;
    }
  },
  methods: {
    deltaChange (symbol, value, key) {

      this.list = this.list.map(row => {
        if (row.symbol === symbol) {
          row[key] = value;
        }
        return row;
      });
    },
    formatDate(date) {

      return DateTime.fromISO(date).setLocale(this.dateFormatLocale).toFormat('DD');
    },
    priceSortValue() {
      return this.activeButton === 'tradingHours' ? 'changeP' : this.activeButton === 'preMarket' ? 'tradingData?.preMarket.changeP' : 'tradingData?.postMarket.changeP';
    },
    volumeSortValue() {
      return this.activeButton === 'tradingHours' ? 'volume' : this.activeButton === 'preMarket' ? 'tradingData?.preMarket.volume' : 'tradingData?.postMarket.volume';
    },
    setTab(tab) {

      this.categoryTabActive = tab;

      if (tab === 'gainers') {
        this.defaultSort = this.priceSortValue();
        this.defaultSortDirection = -1;
      }
      if (tab === 'losers') {
        this.defaultSort = this.priceSortValue();
        this.defaultSortDirection = 1;
      }
      if (tab === 'active') {
        this.defaultSort = this.volumeSortValue();
        this.defaultSortDirection = -1;
      }

      this.tableKey++;
    },
    async addRemoveWatchlistSymbol(symbol) {

      if (this.watchlist.watchlist.includes(symbol)) {

        try {
          mixpanel.track('symbol:remove', {client: window.location.host, symbol: symbol});
        } catch (e) {
        }

        const watchlist = await UserService.removeSymbolFromWatchlist(symbol)
        this.watchlist.updateWatchlist(watchlist);
      } else {

        try {
          mixpanel.track('symbol:add', {client: window.location.host, symbol: symbol});
        } catch (e) {
        }
        const watchlist = await UserService.addSymbolToWatchlist(symbol)
        this.watchlist.updateWatchlist(watchlist);
      }
    },
    async loadTop(showLoading = true, shouldLeave = true) {

      if (showLoading) this.isLoading = true;

      this.leaveStream('topten');
      this.topTen = await TopTen.getTopTenPro(this.tab, this.activeButton);

      this.gainers = this.topTen.gainers;
      this.losers = this.topTen.losers;
      this.active = this.topTen.active;

      let symbols = [
        ...this.gainers.map(item => item.symbol),
        ...this.losers.map(item => item.symbol),
        ...this.active.map(item => item.symbol),
      ];

      this.updateStream(symbols, 'topten');
      // this.$emit('topLoaded', Array.from(symbols));
      if (showLoading) this.isLoading = false;
    }
  },

  beforeUnmount() {
    this.leaveStream('topten');
    clearInterval(this.loadingInterval);
  },
  beforeMount() {
    // this.loadTop().then().catch();
    // this.loadingInterval = setInterval(() => {
    //
    //   this.loadTop(false).then().catch()
    // }, 1000 * 60);
  }
}
</script>

<style scoped lang="scss">

@import "../../assets/style/vars";
@import "../../assets/style/calendar";
@import "../../assets/style/general";

.tab {
  width: 33%;

}

.symbolRow {
  border-bottom: 1px solid var(--notficationBorderColor);
  padding: $smallGap;

  &:last-of-type {
    border-bottom: none;
  }
}

h2 {
  margin: 0 $smallGap $smallGap;
  padding: 0;
}

h3 {
  color: var(--textColor);
  margin: 0;
  padding: 5px 10px;

}

#topTable {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;

  section {
    width: 100%;
  }
}

.symbolTd {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;

  a {
    z-index: 1;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }
}

.addRemoveSymbol {
  flex: 0 0 55px !important;
  width: 45px;
  margin-left: auto;

  &[dir="rtl"] {
    margin-left: unset;
    margin-right: auto;
  }

  i {
    display: none;
  }

  @media(max-width: 660px) {

    flex: 0 0 30px !important;
    width: 30px;
    height: 20px;
    box-sizing: border-box;
    justify-content: center;
    align-items: center;
    i {
      display: block;
    }

    span {
      //display: none;
    }
  }
}

.tabs.round {
  z-index: 2;
  padding: 3px 5px !important;
  margin: 0 !important;
}

#filterPanel {
  padding: 0 10px;
  display: flex;
  align-items: center;
  gap: 1rem;

  #flexBlock {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  @media(max-width: 660px) {
    flex-direction: column;
    //white-space: nowrap;
    //flex-wrap: nowrap;
    //overflow-x: auto;
  }

}

#topBar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--notficationBorderColor) !important;

  @media(max-width: 660px) {
    display: block;
  }
}

.tabs:not(.round) {
  position: unset;
}

.afterMarket {
  color: var(--afterMarketColor);
}

</style>
